import {
  AlertTriangle,
  Archive,
  ArchiveRestore,
  ArrowDown,
  Radar,
  Send,
  Trash2,
} from "lucide-react";
import React from "react";
import { Link, useLocation } from "react-router-dom";

import type * as ozon from "@/api/ozonSupply";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { fmtDate, fmtDateTime, int0 } from "../format";
import { OnecMark, StageBadge } from "./ozonBadges";
import { actionsFor, stageOf } from "./ozonStage";
import {
  ONEC_DOC_LABEL,
  attentionReasons,
  groupByRun,
  onecDocLine,
  onecDocsView,
  onecDocTone,
  onecToneWord,
  ozonStateLabel,
  type OnecDocTone,
  type RegistrySort,
} from "./registryModel";
import { dayLabel, fmtSlot } from "./slotFormat";

/** Что строка умеет сделать — обработчики страницы. */
export interface RowActions {
  draft: (shipmentId: number) => void;
  archive: (shipmentId: number, back: boolean) => void;
  /** идёт вызов по этой поставке — гасим её кнопки, а не все */
  busy: (shipmentId: number) => boolean;
}

/** Заголовок группы «фиксация»: сколько всего поставок в ней и удаление. */
export interface RunInfo {
  /** поставок в фиксации всего (по списку фиксаций); null — список не пришёл */
  total: number | null;
  onDelete: (runId: number, shown: number) => void;
  deleting: (runId: number) => boolean;
}

const COLS = 10;

/** Таблица реестра. ⚠️ Одна `<table>` и в группировке: заголовок фиксации — строка внутри
 *  неё, иначе колонки соседних групп разъезжались бы по ширине своих данных. */
export function RegistryTable({
  rows,
  grouped,
  sort,
  onSort,
  act,
  runInfo,
  now,
  watched,
}: {
  rows: ozon.OzonRegistryRow[];
  grouped: boolean;
  sort: RegistrySort;
  onSort: (s: RegistrySort) => void;
  act: RowActions;
  runInfo: (runId: number) => RunInfo;
  now: number;
  /** активные мониторинги мест по поставке; null — список не прочитался (отметок нет вовсе) */
  watched: ReadonlyMap<number, ozon.SlotWatchView> | null;
}): React.ReactElement {
  const th = "px-2 py-1.5 font-normal whitespace-nowrap";
  return (
    // узкий экран — прокрутка таблицы, а не перенос колонок друг под друга
    <div className="overflow-x-auto rounded-md border">
      <table className="w-full min-w-[1150px] border-collapse text-sm">
        <thead className="bg-muted/40 text-left text-xs text-muted-foreground">
          <tr>
            <th className={th}>Фикс.</th>
            <th className={th}>Регион</th>
            <th className={th}>Юрлицо</th>
            <th className={th}>Стадия</th>
            <th className={th}>Ozon</th>
            <th className={th}>Поставка</th>
            <th className={th}>
              <button
                type="button"
                className={cn(
                  "inline-flex items-center gap-1 hover:text-foreground",
                  sort === "slot" && "text-foreground",
                )}
                title={
                  sort === "slot"
                    ? "Отсортировано по дате приёмки — нажмите, чтобы вернуть по фиксации"
                    : "Сортировать по дате приёмки"
                }
                onClick={() => onSort(sort === "slot" ? "run" : "slot")}
              >
                Приёмка
                {sort === "slot" && <ArrowDown className="size-3" />}
              </button>
            </th>
            <th className={cn(th, "text-right")}>Просим → возьмут</th>
            <th className={th}>1С</th>
            <th className={th} />
          </tr>
        </thead>
        {grouped ? (
          groupByRun(rows).map((g) => (
            <tbody key={g.run_id}>
              <GroupHeader g={g} info={runInfo(g.run_id)} />
              {g.rows.map((r) => (
                <Row
                  key={r.shipment_id}
                  r={r}
                  act={act}
                  now={now}
                  watch={watched?.get(r.shipment_id) ?? null}
                  watchKnown={watched !== null}
                />
              ))}
            </tbody>
          ))
        ) : (
          <tbody>
            {rows.map((r) => (
              <Row
                key={r.shipment_id}
                r={r}
                act={act}
                now={now}
                watch={watched?.get(r.shipment_id) ?? null}
                  watchKnown={watched !== null}
              />
            ))}
          </tbody>
        )}
      </table>
    </div>
  );
}

function GroupHeader({
  g,
  info,
}: {
  g: ReturnType<typeof groupByRun>[number];
  info: RunInfo;
}): React.ReactElement {
  const shown = g.rows.length;
  return (
    <tr className="border-t bg-muted/60">
      <td colSpan={COLS} className="px-2 py-1.5">
        <div className="flex items-center gap-2 text-sm">
          <span className="font-medium">Фиксация #{g.run_id}</span>
          <span className="text-muted-foreground">
            · {fmtDateTime(g.run_created_at)} · {g.run_created_by} · поставок{" "}
            {shown}
            {info.total !== null && info.total !== shown && ` из ${info.total}`}
          </span>
          <button
            type="button"
            disabled={info.deleting(g.run_id)}
            onClick={() => info.onDelete(g.run_id, shown)}
            className="ml-auto rounded p-1 text-muted-foreground hover:bg-red-100 hover:text-red-600 disabled:opacity-50 dark:hover:bg-red-950 dark:hover:text-red-400"
            title="Удалить фиксацию со всеми поставками"
          >
            <Trash2 className="size-4" />
          </button>
        </div>
      </td>
    </tr>
  );
}

function Row({
  r,
  act,
  now,
  watch,
  watchKnown,
}: {
  r: ozon.OzonRegistryRow;
  act: RowActions;
  now: number;
  /** активный мониторинг мест этой поставки */
  watch: ozon.SlotWatchView | null;
  /** пока мониторинги не прочитались (или чтение упало), «есть ли мониторинг» неизвестно */
  watchKnown: boolean;
}): React.ReactElement {
  const { search } = useLocation();
  const stage = stageOf(r, now);
  const primary = actionsFor(stage, r).primary;
  // «Спросить площадку» — единственное действие без ввода: черновик обратим и в кабинете не виден.
  // ⚠️ По одной строке, массовой кнопки нет: черновики ограничены (2 в минуту, 50 в час), и
  // «спросить по всем» упёрлось бы в лимит, оставив половину в неизвестном состоянии
  // ⚠️ Под мониторингом мест черновик ведёт наблюдатель, и бэкенд ответит 409 — кнопки нет
  const ask = !r.archived && watchKnown && !watch && primary?.key === "draft" ? primary : null;
  const href = `/supply/ozon/${r.shipment_id}`;
  const busy = act.busy(r.shipment_id);
  const why = attentionReasons(r, now);
  const td = "px-2 py-1.5 align-middle";
  return (
    <>
      <tr className={cn("border-t", why.length > 0 && "bg-amber-50/50 dark:bg-amber-950/20")}>
        <td
          className={cn(td, "whitespace-nowrap text-muted-foreground")}
          title={`Фиксация #${r.run_id} · ${fmtDateTime(r.run_created_at)} · ${r.run_created_by}`}
        >
          #{r.run_id} · {fmtDate(r.run_created_at).slice(0, 5)}
        </td>
        <td className={td}>
          <span className="inline-flex items-center gap-1">
            {why.length > 0 && (
              <span
                title={`Внимание: ${why.join("; ")}`}
                aria-label={`Внимание: ${why.join("; ")}`}
              >
                <AlertTriangle className="size-3.5 shrink-0 text-amber-600 dark:text-amber-400" />
              </span>
            )}
            <Link
              to={href}
              state={{ from: search }}
              className="font-medium hover:underline"
            >
              {r.region}
            </Link>
          </span>
        </td>
        <td className={cn(td, "text-xs text-muted-foreground")}>
          {r.legal_name}
        </td>
        <td className={td}>
          <span className="inline-flex flex-wrap items-center gap-1">
            <StageBadge stage={stage} state={r.state} />
            {watch && (
              <span
                className="inline-flex items-center gap-1 rounded bg-sky-100 px-1.5 py-0.5 text-xs text-sky-800 dark:bg-sky-950 dark:text-sky-300"
                title={`Мониторинг мест: пункт ${watch.drop_off_name}, окна не раньше чем через ${watch.not_before_hours} ч — наблюдатель сам создаст заявку`}
              >
                <Radar className="size-3" /> мониторинг мест
              </span>
            )}
          </span>
        </td>
        <td className={td}>
          <OzonState r={r} />
        </td>
        <td className={cn(td, "whitespace-nowrap")}>
          {r.supply_number ?? <span className="text-muted-foreground">—</span>}
        </td>
        <td
          className={cn(td, "whitespace-nowrap")}
          title={
            r.slot_from
              ? `${fmtSlot(r.slot_from)}${r.slot_to ? ` — ${fmtSlot(r.slot_to)}` : ""}`
              : undefined
          }
        >
          {r.slot_from ? (
            dayLabel(r.slot_from.slice(0, 10))
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </td>
        <td className={cn(td, "whitespace-nowrap text-right tabular-nums")}>
          {int0(r.requested)}
          {r.state !== "" && (
            <>
              {" → "}
              <b
                className={cn(
                  r.dropped > 0
                    ? "text-amber-600 dark:text-amber-400"
                    : "text-foreground",
                )}
              >
                {int0(r.accepted)}
              </b>
            </>
          )}
        </td>
        <td className={td}>
          {r.onec ? (
            <OnecCell r={r} onec={r.onec} />
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </td>
        <td className={cn(td, "whitespace-nowrap text-right")}>
          <div className="inline-flex items-center gap-1">
            {ask ? (
              <Button
                size="sm"
                className="h-7 px-2 text-xs"
                variant={r.state === "" ? "default" : "outline"}
                disabled={busy}
                title={ask.hint}
                onClick={() => act.draft(r.shipment_id)}
              >
                <Send className={cn("size-3.5", busy && "animate-pulse")} />
                {ask.label}
              </Button>
            ) : (
              <Button
                size="sm"
                variant="outline"
                className="h-7 px-2 text-xs"
                asChild
              >
                <Link to={href} state={{ from: search }}>
                  Открыть →
                </Link>
              </Button>
            )}
            {/* ⚠️ «В архив» — только у подготовки: у поставки с заявкой есть живое окно у
                площадки, и прятать её из списка значило бы забыть про неё (бэкенд ответит 409) */}
            {!r.archived && r.group === "prep" && r.state !== "ordering" && (
              <Button
                size="sm"
                variant="ghost"
                className="h-7 px-2 text-xs"
                disabled={busy}
                title="Убрать в архив: поставку не будем делать. Вернуть можно из «Архива»"
                onClick={() => act.archive(r.shipment_id, false)}
              >
                <Archive className="size-3.5" />В архив
              </Button>
            )}
            {r.archived_manually && (
              <Button
                size="sm"
                variant="ghost"
                className="h-7 px-2 text-xs"
                disabled={busy}
                title="Вернуть из архива в активные"
                onClick={() => act.archive(r.shipment_id, true)}
              >
                <ArchiveRestore className="size-3.5" />
                Вернуть
              </Button>
            )}
          </div>
        </td>
      </tr>
      {/* ⚠️ Поломанный цикл 1С — КРАСНОЙ строкой: `onec` у такой поставки приходит пустым, и
          без неё колонка «1С» молча говорила бы «в учёте ничего нет» про поставку с заказом */}
      {r.broken && (
        <tr>
          <td
            colSpan={COLS}
            className="px-2 pb-1.5 text-xs text-red-600 dark:text-red-400"
          >
            <span className="inline-flex items-center gap-1.5">
              <AlertTriangle className="size-3.5 shrink-0" /> {r.broken}
            </span>
          </td>
        </tr>
      )}
    </>
  );
}

/** Статус заявки у площадки — по-русски, код и время обновления в подсказке. */
function OzonState({ r }: { r: ozon.OzonRegistryRow }): React.ReactElement {
  if (!r.order_id)
    return (
      <span className="text-xs text-muted-foreground" title="Заявки нет — спрашивать площадку не о чем">
        —
      </span>
    );
  const label = ozonStateLabel(r.ozon_order_state);
  if (label === null)
    return (
      <span
        className="text-xs text-muted-foreground"
        title="Статус заявки ещё не спрашивали — кнопка «Обновить статусы»"
      >
        не обновлялся
      </span>
    );
  return (
    <span
      className={cn(
        "text-xs",
        r.group === "unknown" && "font-mono text-amber-700 dark:text-amber-300",
      )}
      title={`${r.ozon_order_state} · обновлено ${fmtDateTime(r.ozon_order_state_at)}`}
    >
      {label}
    </span>
  );
}

const TONE_CLS: Record<OnecDocTone, string> = {
  posted:
    "border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-300",
  unposted:
    "border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300",
  deleted:
    "border-red-300 bg-red-50 text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300",
  missing:
    "border-red-300 bg-red-50 text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300",
  unknown: "border-input bg-muted text-muted-foreground",
};

/** Ячейка «1С»: снимок цепочки из 1С, если кнопкой его брали, иначе — что записано у нас.
 *  Снимок сверяется с текущими номерами (`onecDocsView`): документ, созданный или замененный
 *  после снимка, показывается по нашей записи с пометкой «состояние в 1С не запрашивалось».
 *
 *  ⚠️ Без снимка — прежняя пометка И слова «в 1С не запрашивалось»: иначе «заказ 00НФ-…» выглядел
 *  бы подтверждённым 1С, хотя это только наша запись о нём. */
function OnecCell({
  r,
  onec,
}: {
  r: ozon.OzonRegistryRow;
  onec: ozon.OzonOnec;
}): React.ReactElement {
  const view = onecDocsView(r) ?? [];
  const NOT_ASKED = "состояние в 1С не запрашивалось";
  if (!view.some((v) => v.doc))
    return (
      <span className="inline-flex flex-col">
        <OnecMark onec={onec} />
        <span
          className="text-[11px] text-muted-foreground"
          title="Состояние документов в 1С ещё не спрашивали — кнопка «Обновить статусы»"
        >
          {NOT_ASKED}
        </span>
      </span>
    );
  const title = [
    ...view.map((v) =>
      v.doc || v.number === null
        ? onecDocLine(v.kind, v.doc)
        : `${ONEC_DOC_LABEL[v.kind].full.charAt(0).toLocaleUpperCase("ru")}${ONEC_DOC_LABEL[v.kind].full.slice(1)} ${v.number}: ${NOT_ASKED}`,
    ),
    `по данным 1С на ${fmtDateTime(r.onec_docs_at)}`,
  ].join("\n");
  const order = view.find((v) => v.kind === "order")!;
  const rest = view.filter((v) => v.kind !== "order");
  const o = order.doc;
  const oTone = o ? onecDocTone(o) : null;
  return (
    <span
      className="inline-flex flex-wrap items-center gap-1 text-xs"
      title={title}
      data-testid="onec-docs"
    >
      <span className="whitespace-nowrap">
        Заказ {order.number}
        {o?.found && ` · ${o.state ?? "состояние не указано"}`}
      </span>
      {o && oTone !== "posted" && oTone !== null && (
        <DocTag kind="order" tone={oTone} />
      )}
      {!o && <NotAskedTag text="Заказ: не запрашивалось" />}
      {rest.map(({ kind: k, number, doc: d }) =>
        d ? (
          <DocTag key={k} kind={k} tone={onecDocTone(d)} />
        ) : number !== null ? (
          <NotAskedTag
            key={k}
            text={`${ONEC_DOC_LABEL[k].short} ${number}: не запрашивалось`}
          />
        ) : (
          <span
            key={k}
            className="whitespace-nowrap rounded border border-dashed px-1 text-[11px] text-muted-foreground"
          >
            {ONEC_DOC_LABEL[k].short}:{" "}
            {k === "transfer" ? "не создана" : "не создан"}
          </span>
        ),
      )}
    </span>
  );
}

/** Нейтральная метка: документ есть у нас, а в 1С его состояние не спрашивали (не ошибка). */
function NotAskedTag({ text }: { text: string }): React.ReactElement {
  return (
    <span className="whitespace-nowrap rounded border px-1 text-[11px] text-muted-foreground">
      {text}
    </span>
  );
}

/** Метка документа: короткое имя и состояние СЛОВАМИ, цвет — только подкрепление. */
function DocTag({
  kind,
  tone,
}: {
  kind: ozon.OnecDocKind;
  tone: OnecDocTone;
}): React.ReactElement {
  const short = ONEC_DOC_LABEL[kind].short;
  return (
    <span
      className={cn(
        "whitespace-nowrap rounded border px-1 text-[11px]",
        TONE_CLS[tone],
      )}
    >
      {kind === "order" ? "Заказ" : short}: {onecToneWord(kind, tone)}
    </span>
  );
}
